import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../utils/apiUrl';
import { FiSearch, FiChevronDown, FiChevronLeft, FiChevronRight, FiX, FiPackage, FiUser, FiCalendar, FiHash, FiSettings, FiTrash2, FiPlus, FiCheck, FiDollarSign } from 'react-icons/fi';
import './UnimicroOrdersStyles.css';

const UnimicroOrdersWidget = ({ token, onClose }) => {
  const apiUrl = getApiUrl();

  const [sellers, setSellers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orderLines, setOrderLines] = useState(null);
  const [linesLoading, setLinesLoading] = useState(false);

  const [filters, setFilters] = useState({
    sellerNr: '',
    statuses: [],
    search: '',
    dateFrom: '',
    dateTo: '',
    dateField: 'orderDate',
    minNOK: '',
    maxNOK: '',
    page: 1,
    pageSize: 100,
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Settings panel state
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState('mapping');
  const [sellerMappings, setSellerMappings] = useState([]);
  const [localUsers, setLocalUsers] = useState([]);
  const [excludedCustomers, setExcludedCustomers] = useState([]);
  const [excludedOrders, setExcludedOrders] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [newExcludedKundenr, setNewExcludedKundenr] = useState('');
  const [newExcludedReason, setNewExcludedReason] = useState('');
  const [newExcludedOrdrenr, setNewExcludedOrdrenr] = useState('');
  const [newExcludedOrderReason, setNewExcludedOrderReason] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const formatNOK = (value) => {
    if (value === null || value === undefined) return '—';
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Intl.NumberFormat('nb-NO').format(0) && new Date(dateStr).toLocaleDateString('nb-NO');
  };

  const statusColors = {
    'Registrert': { bg: '#dbeafe', text: '#1e40af' },
    'Fakturert': { bg: '#dcfce7', text: '#059669' },
    'Del-fakturert': { bg: '#fef3c7', text: '#92400e' },
    'Slettet ordre': { bg: '#fee2e2', text: '#991b1b' },
  };

  const getStatusStyle = (statusText) => statusColors[statusText] || { bg: '#f1f5f9', text: '#475569' };

  // Fetch sellers and statuses on mount
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [sellersRes, statusesRes] = await Promise.all([
          fetch(`${apiUrl}/unimicro/sellers`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${apiUrl}/unimicro/order-statuses`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (sellersRes.ok) setSellers(await sellersRes.json());
        if (statusesRes.ok) setStatuses(await statusesRes.json());
      } catch (e) {
        console.error('Failed to fetch metadata:', e);
      }
    };
    fetchMeta();
  }, [apiUrl, token]);

  // Fetch orders when filters change
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setExpandedOrder(null);
    setOrderLines(null);
    try {
      const params = new URLSearchParams();
      if (filters.sellerNr) params.append('sellerNr', filters.sellerNr);
      if (filters.statuses.length > 0) params.append('statuses', filters.statuses.join(','));
      if (filters.search) params.append('search', filters.search);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.dateField) params.append('dateField', filters.dateField);
      if (filters.minNOK) params.append('minNOK', filters.minNOK);
      if (filters.maxNOK) params.append('maxNOK', filters.maxNOK);
      params.append('page', filters.page);
      params.append('pageSize', filters.pageSize);
      if (sortField) {
        params.append('sortField', sortField);
        params.append('sortDir', sortDir);
      }

      const res = await fetch(`${apiUrl}/unimicro/orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
        setTotal(data.total || 0);
      } else {
        setOrders([]);
        setTotal(0);
      }
    } catch (e) {
      console.error('Failed to fetch orders:', e);
      setOrders([]);
      setTotal(0);
    }
    setLoading(false);
  }, [apiUrl, token, filters, sortField, sortDir]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, page: 1 }));
    fetchOrders();
  };

  const handleClearFilters = () => {
    setFilters({ sellerNr: '', statuses: [], search: '', dateFrom: '', dateTo: '', dateField: 'orderDate', minNOK: '', maxNOK: '', page: 1, pageSize: 100 });
  };

  const handleStatusToggle = (statusId) => {
    setFilters(prev => {
      const id = String(statusId);
      const current = prev.statuses;
      const next = current.includes(id) ? current.filter(s => s !== id) : [...current, id];
      return { ...prev, statuses: next, page: 1 };
    });
  };

  const hasActiveFilters = filters.sellerNr || filters.statuses.length > 0 || filters.search || filters.dateFrom || filters.dateTo || filters.minNOK || filters.maxNOK;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  // --- Settings functions ---
  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const [mappingsRes, usersRes, excludedRes, excludedOrdersRes] = await Promise.all([
        fetch(`${apiUrl}/unimicro/seller-mappings`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiUrl}/inquiries/users/can-create-work-orders`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiUrl}/unimicro/excluded-customers`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiUrl}/unimicro/excluded-orders`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (mappingsRes.ok) setSellerMappings(await mappingsRes.json());
      if (usersRes.ok) setLocalUsers(await usersRes.json());
      if (excludedRes.ok) setExcludedCustomers(await excludedRes.json());
      if (excludedOrdersRes.ok) setExcludedOrders(await excludedOrdersRes.json());
    } catch (e) {
      console.error('Failed to fetch settings:', e);
    }
    setSettingsLoading(false);
  }, [apiUrl, token]);

  const handleOpenSettings = () => {
    setShowSettings(true);
    fetchSettings();
  };

  const handleSaveMapping = async (sellerNr, sellerName, localUserId) => {
    try {
      const res = await fetch(`${apiUrl}/unimicro/seller-mappings/${sellerNr}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sellerName, localUserId: localUserId || null }),
      });
      if (res.ok) setSellerMappings(await res.json());
    } catch (e) {
      console.error('Failed to save mapping:', e);
    }
  };

  const handleDeleteMapping = async (sellerNr) => {
    try {
      const res = await fetch(`${apiUrl}/unimicro/seller-mappings/${sellerNr}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSellerMappings(await res.json());
    } catch (e) {
      console.error('Failed to delete mapping:', e);
    }
  };

  const handleAddExcluded = async () => {
    if (!newExcludedKundenr.trim()) return;
    try {
      const res = await fetch(`${apiUrl}/unimicro/excluded-customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ kundenr: newExcludedKundenr.trim(), kundenavn: '', reason: newExcludedReason.trim() || null }),
      });
      if (res.ok) {
        setExcludedCustomers(await res.json());
        setNewExcludedKundenr('');
        setNewExcludedReason('');
      }
    } catch (e) {
      console.error('Failed to add excluded customer:', e);
    }
  };

  const handleRemoveExcluded = async (kundenr) => {
    try {
      const res = await fetch(`${apiUrl}/unimicro/excluded-customers/${kundenr}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setExcludedCustomers(await res.json());
    } catch (e) {
      console.error('Failed to remove excluded customer:', e);
    }
  };

  const handleAddExcludedOrder = async () => {
    if (!newExcludedOrdrenr.trim()) return;
    try {
      const res = await fetch(`${apiUrl}/unimicro/excluded-orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ordrenr: newExcludedOrdrenr.trim(), reason: newExcludedOrderReason.trim() || null }),
      });
      if (res.ok) {
        setExcludedOrders(await res.json());
        setNewExcludedOrdrenr('');
        setNewExcludedOrderReason('');
      }
    } catch (e) {
      console.error('Failed to add excluded order:', e);
    }
  };

  const handleRemoveExcludedOrder = async (ordrenr) => {
    try {
      const res = await fetch(`${apiUrl}/unimicro/excluded-orders/${ordrenr}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setExcludedOrders(await res.json());
    } catch (e) {
      console.error('Failed to remove excluded order:', e);
    }
  };

  // Fuzzy name matching for auto-suggest
  const normalizeName = (name) => (name || '').toLowerCase().trim().replace(/\./g, '').replace(/,/g, '').replace(/\s+/g, ' ');
  const fuzzyMatch = (uniName, localName) => {
    const u = normalizeName(uniName);
    const l = normalizeName(localName);
    if (u === l) return true;
    const uParts = u.split(' ');
    const lParts = l.split(' ');
    if (uParts.length >= 2 && lParts.length >= 2) {
      return uParts[0].slice(0, 3) === lParts[0].slice(0, 3) && uParts[uParts.length - 1] === lParts[lParts.length - 1];
    }
    return false;
  };

  const getAutoSuggestedUserId = (sellerName) => {
    for (const u of localUsers) {
      if (fuzzyMatch(sellerName, u.name)) return u.id;
    }
    return null;
  };

  const getMappingForSeller = (sellerNr) => {
    return sellerMappings.find(m => m.seller_nr === sellerNr);
  };

  const handleRowClick = async (orderNumber) => {
    if (expandedOrder === orderNumber) {
      setExpandedOrder(null);
      setOrderLines(null);
      return;
    }
    setExpandedOrder(orderNumber);
    setLinesLoading(true);
    setOrderLines(null);
    try {
      const res = await fetch(`${apiUrl}/unimicro/orders/${orderNumber}/full`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOrderLines(data);
      }
    } catch (e) {
      console.error('Failed to fetch order lines:', e);
    }
    setLinesLoading(false);
  };

  const totalPages = Math.ceil(total / filters.pageSize);
  const startIdx = (filters.page - 1) * filters.pageSize + 1;
  const endIdx = Math.min(filters.page * filters.pageSize, total);

  return (
    <div className="uo-container tw-flex tw-flex-col tw-h-full">
      {/* Toolbar */}
      <div className="uo-toolbar">
        <div className="uo-toolbar-heading">
          <div className="uo-toolbar-icon"><FiPackage size={18} color="#fff" /></div>
          <div>
            <h2 className="uo-toolbar-title">Unimicro Ordrer</h2>
            <p className="uo-toolbar-subtitle">Vis og filtrer ordrer fra Unimicro</p>
          </div>
        </div>
        {onClose && (
          <button type="button" className="uo-toolbar-close" onClick={onClose}>
            ✕ Lukk
          </button>
        )}
      </div>

      {/* Filter bar */}
      <div className="uo-filterbar tw-bg-white tw-border-b tw-border-slate-200 tw-px-6 tw-py-4">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-2">
            <FiUser size={16} className="tw-text-slate-400" />
            <select
              value={filters.sellerNr}
              onChange={(e) => handleFilterChange('sellerNr', e.target.value)}
              className="tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-slate-300 tw-rounded-lg tw-bg-white tw-outline-none focus:tw-border-blue-500"
            >
              <option value="">Alle selgere</option>
              {sellers.map(s => (
                <option key={s.Nr} value={s.Nr}>{s.Navn}</option>
              ))}
            </select>
          </div>

          {/* Multi-status dropdown */}
          <div className="tw-relative" style={{ minWidth: '160px' }}>
            <div className="tw-flex tw-items-center tw-gap-2">
              <FiPackage size={16} className="tw-text-slate-400" />
              <div className="tw-relative tw-flex-1">
                <button
                  type="button"
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className="tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-slate-300 tw-rounded-lg tw-bg-white tw-outline-none focus:tw-border-blue-500 tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-2"
                >
                  <span className="tw-truncate">
                    {filters.statuses.length === 0 ? 'Alle statuser' : filters.statuses.length === 1 ? statuses.find(s => String(s.ID) === filters.statuses[0])?.Beskrivelse || 'Status' : `${filters.statuses.length} valgt`}
                  </span>
                  <FiChevronDown size={14} className="tw-text-slate-400" style={{ transform: showStatusDropdown ? 'rotate(180deg)' : '', transition: 'transform 0.2s' }} />
                </button>
                {showStatusDropdown && (
                  <>
                    <div className="tw-fixed tw-inset-0 tw-z-40" onClick={() => setShowStatusDropdown(false)} />
                    <div className="tw-absolute tw-top-full tw-left-0 tw-mt-1 tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg tw-shadow-lg tw-z-50 tw-p-2 tw-min-w-[200px]">
                      {statuses.map(s => (
                        <label key={s.ID} className="tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1.5 hover:tw-bg-slate-50 tw-rounded tw-cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filters.statuses.includes(String(s.ID))}
                            onChange={() => handleStatusToggle(s.ID)}
                            className="tw-cursor-pointer"
                          />
                          <span className="tw-text-sm tw-text-slate-700">{s.Beskrivelse} ({s.ID})</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-[200px]">
            <FiSearch size={16} className="tw-text-slate-400" />
            <input
              type="text"
              placeholder="Søk på ordrenr eller kunde..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-slate-300 tw-rounded-lg tw-bg-white tw-outline-none focus:tw-border-blue-500 tw-w-full"
            />
          </div>

          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-600 tw-border tw-border-slate-300 tw-rounded-lg hover:tw-bg-slate-50"
            title="Avanserte filtre"
          >
            <FiCalendar size={14} />
            Filtre
            <FiChevronDown size={12} style={{ transform: showAdvancedFilters ? 'rotate(180deg)' : '', transition: 'transform 0.2s' }} />
          </button>

          <button
            onClick={handleSearch}
            className="tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-bg-blue-600 tw-rounded-lg hover:tw-bg-blue-700 tw-transition-colors"
          >
            Søk
          </button>

          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-500 hover:tw-text-slate-700"
            >
              <FiX size={14} /> Nullstill
            </button>
          )}

          <button
            onClick={handleOpenSettings}
            className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-600 tw-border tw-border-slate-300 tw-rounded-lg hover:tw-bg-slate-50 tw-ml-auto"
            title="Innstillinger"
          >
            <FiSettings size={16} /> Innstillinger
          </button>
        </div>

        {/* Advanced filters */}
        {showAdvancedFilters && (
          <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-slate-100 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <div className="tw-flex tw-items-center tw-gap-2">
              <select
                value={filters.dateField}
                onChange={(e) => handleFilterChange('dateField', e.target.value)}
                className="tw-px-2 tw-py-1.5 tw-text-xs tw-border tw-border-slate-300 tw-rounded-lg tw-bg-white"
              >
                <option value="orderDate">Ordredato</option>
                <option value="deliveryDate">Leveringsdato</option>
              </select>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="tw-px-2 tw-py-1.5 tw-text-xs tw-border tw-border-slate-300 tw-rounded-lg tw-bg-white"
                placeholder="Fra"
              />
              <span className="tw-text-slate-400 tw-text-xs">—</span>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="tw-px-2 tw-py-1.5 tw-text-xs tw-border tw-border-slate-300 tw-rounded-lg tw-bg-white"
                placeholder="Til"
              />
            </div>

            <div className="tw-flex tw-items-center tw-gap-2">
              <FiDollarSign size={14} className="tw-text-slate-400" />
              <input
                type="number"
                value={filters.minNOK}
                onChange={(e) => handleFilterChange('minNOK', e.target.value)}
                placeholder="Min NOK"
                className="tw-px-2 tw-py-1.5 tw-text-xs tw-border tw-border-slate-300 tw-rounded-lg tw-bg-white tw-w-24"
              />
              <span className="tw-text-slate-400 tw-text-xs">—</span>
              <input
                type="number"
                value={filters.maxNOK}
                onChange={(e) => handleFilterChange('maxNOK', e.target.value)}
                placeholder="Max NOK"
                className="tw-px-2 tw-py-1.5 tw-text-xs tw-border tw-border-slate-300 tw-rounded-lg tw-bg-white tw-w-24"
              />
            </div>
          </div>
        )}
      </div>

      {/* Orders table */}
      <div className="tw-flex-1 tw-overflow-auto tw-px-6 tw-py-4">
        {loading ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-h-32">
            <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-h-32 tw-text-slate-400 tw-text-sm">
            Ingen ordrer funnet
          </div>
        ) : (
          <table className="tw-w-full tw-text-sm">
            <thead>
              <tr className="tw-border-b tw-border-slate-200 tw-text-left">
                {[
                  { field: 'orderNumber', label: 'Ordrenr', align: '' },
                  { field: 'customerName', label: 'Kunde', align: '' },
                  { field: 'sellerName', label: 'Selger', align: '' },
                  { field: 'status', label: 'Status', align: '' },
                  { field: 'orderDate', label: 'Ordredato', align: '' },
                  { field: 'deliveryDate', label: 'Lever.dato', align: '' },
                  { field: 'totalNOK', label: 'NOK', align: 'tw-text-right' },
                ].map(col => (
                  <th
                    key={col.field}
                    onClick={() => handleSort(col.field)}
                    className={`tw-py-2 tw-px-3 tw-font-semibold tw-text-slate-600 tw-cursor-pointer hover:tw-text-slate-900 tw-select-none tw-whitespace-nowrap ${col.align}`}
                  >
                    {col.label}
                    {sortField === col.field && (
                      <span className="tw-ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                ))}
                <th className="tw-py-2 tw-px-3 tw-w-8"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o, idx) => (
                <React.Fragment key={idx}>
                  <tr
                    onClick={() => handleRowClick(o.orderNumber)}
                    className="tw-border-b tw-border-slate-100 hover:tw-bg-slate-50 tw-cursor-pointer tw-transition-colors"
                  >
                    <td className="tw-py-2.5 tw-px-3 tw-font-semibold tw-text-slate-800">{o.orderNumber}</td>
                    <td className="tw-py-2.5 tw-px-3 tw-text-slate-600 tw-max-w-[200px] tw-truncate">{o.customerName || '—'}</td>
                    <td className="tw-py-2.5 tw-px-3 tw-text-slate-600">{o.sellerName || '—'}</td>
                    <td className="tw-py-2.5 tw-px-3">
                      <span
                        className="tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-semibold"
                        style={getStatusStyle(o.statusText)}
                      >
                        {o.statusText || `Status ${o.status}`}
                      </span>
                    </td>
                    <td className="tw-py-2.5 tw-px-3 tw-text-slate-500">{formatDate(o.orderDate)}</td>
                    <td className="tw-py-2.5 tw-px-3 tw-text-slate-500">{formatDate(o.deliveryDate)}</td>
                    <td className="tw-py-2.5 tw-px-3 tw-text-right tw-font-semibold tw-text-slate-800">{formatNOK(o.totalNOK)}</td>
                    <td className="tw-py-2.5 tw-px-2">
                      <FiChevronDown
                        size={16}
                        className="tw-text-slate-300"
                        style={{ transform: expandedOrder === o.orderNumber ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                      />
                    </td>
                  </tr>
                  {expandedOrder === o.orderNumber && (
                    <tr>
                      <td colSpan={8} className="tw-p-0">
                        <div className="tw-bg-slate-100 tw-p-4 tw-border-b tw-border-slate-200">
                          {linesLoading ? (
                            <div className="tw-flex tw-items-center tw-justify-center tw-py-4">
                              <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-b-2 tw-border-blue-600"></div>
                            </div>
                          ) : orderLines && orderLines.lines && orderLines.lines.length > 0 ? (
                            <div>
                              <div className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-mb-2 tw-uppercase">Ordrelinjer</div>
                              <table className="tw-w-full tw-text-xs">
                                <thead>
                                  <tr className="tw-text-left tw-text-slate-500">
                                    <th className="tw-py-1.5 tw-px-2">Varenr</th>
                                    <th className="tw-py-1.5 tw-px-2">Beskrivelse</th>
                                    <th className="tw-py-1.5 tw-px-2 tw-text-right">Antall</th>
                                    <th className="tw-py-1.5 tw-px-2">Enhet</th>
                                    <th className="tw-py-1.5 tw-px-2 tw-text-right">Pris</th>
                                    <th className="tw-py-1.5 tw-px-2 tw-text-right">Sum</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {orderLines.lines.map((line, li) => (
                                    <tr key={li} className="tw-border-b tw-border-slate-200">
                                      <td className="tw-py-1.5 tw-px-2 tw-font-medium tw-text-slate-700">{line.productNumber || '—'}</td>
                                      <td className="tw-py-1.5 tw-px-2 tw-text-slate-600 tw-max-w-[300px]">{line.productName || '—'}</td>
                                      <td className="tw-py-1.5 tw-px-2 tw-text-right tw-text-slate-600">{line.quantity || 0}</td>
                                      <td className="tw-py-1.5 tw-px-2 tw-text-slate-500">{line.unit || '—'}</td>
                                      <td className="tw-py-1.5 tw-px-2 tw-text-right tw-text-slate-600">{formatNOK(line.price)}</td>
                                      <td className="tw-py-1.5 tw-px-2 tw-text-right tw-font-semibold tw-text-slate-800">{formatNOK(line.lineTotal)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              <div className="tw-mt-3 tw-flex tw-justify-between tw-items-center">
                                <div className="tw-text-xs tw-text-slate-500">
                                  {orderLines.order?.fritekst && <div><strong>Fritekst:</strong> {orderLines.order.fritekst}</div>}
                                  {orderLines.order?.kommentar && <div><strong>Kommentar:</strong> {orderLines.order.kommentar}</div>}
                                </div>
                                <div className="tw-text-sm tw-font-bold tw-text-slate-800">
                                  Total: {formatNOK(orderLines.lines.reduce((sum, l) => sum + (l.lineTotal || 0), 0))}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <p className="tw-text-sm tw-text-slate-400 tw-py-2">Ingen ordrelinjer funnet</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="tw-border-t-2 tw-border-slate-300 tw-bg-slate-50">
                <td colSpan={6} className="tw-py-3 tw-px-3 tw-text-right tw-font-bold tw-text-slate-700">
                  Sum ({orders.length} ordrer):
                </td>
                <td className="tw-py-3 tw-px-3 tw-text-right tw-font-bold tw-text-slate-900 tw-text-lg">
                  {formatNOK(orders.reduce((sum, o) => sum + (o.totalNOK || 0), 0))}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="tw-bg-white tw-border-t tw-border-slate-200 tw-px-6 tw-py-3 tw-flex tw-items-center tw-justify-between">
          <div className="tw-text-sm tw-text-slate-500">
            Viser {startIdx}–{endIdx} av {total} ordrer
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              onClick={() => setFilters(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={filters.page === 1}
              className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-border-slate-300 tw-rounded-lg disabled:tw-opacity-40 hover:tw-bg-slate-50"
            >
              <FiChevronLeft size={14} /> Forrige
            </button>
            <span className="tw-text-sm tw-text-slate-600">
              Side {filters.page} / {totalPages}
            </span>
            <button
              onClick={() => setFilters(prev => ({ ...prev, page: Math.min(totalPages, prev.page + 1) }))}
              disabled={filters.page >= totalPages}
              className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-border-slate-300 tw-rounded-lg disabled:tw-opacity-40 hover:tw-bg-slate-50"
            >
              Neste <FiChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-z-50 tw-flex tw-items-center tw-justify-center" onClick={() => setShowSettings(false)}>
          <div className="tw-bg-white tw-rounded-xl tw-shadow-2xl tw-w-[800px] tw-max-h-[80vh] tw-flex tw-flex-col tw-border-2 tw-border-slate-300" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-border-b-2 tw-border-slate-200 tw-bg-slate-50 tw-rounded-t-xl">
              <h2 className="tw-text-lg tw-font-bold tw-text-slate-800">Unimicro Innstillinger</h2>
              <button onClick={() => setShowSettings(false)} className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-lg tw-text-slate-400 hover:tw-text-slate-600 hover:tw-bg-slate-200 tw-transition-colors">
                <FiX size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="tw-flex tw-gap-1 tw-px-8 tw-pt-3 tw-border-b tw-border-slate-200 tw-bg-white">
              <button
                onClick={() => setSettingsTab('mapping')}
                className={`tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-rounded-t-lg tw-border tw-border-b-0 ${settingsTab === 'mapping' ? 'tw-bg-blue-50 tw-text-blue-600 tw-border-blue-300 tw-border-b-2 tw-border-blue-600' : 'tw-text-slate-500 hover:tw-text-slate-700 tw-border-transparent'}`}
              >
                Selger Mapping
              </button>
              <button
                onClick={() => setSettingsTab('exclusion')}
                className={`tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-rounded-t-lg tw-border tw-border-b-0 ${settingsTab === 'exclusion' ? 'tw-bg-blue-50 tw-text-blue-600 tw-border-blue-300 tw-border-b-2 tw-border-blue-600' : 'tw-text-slate-500 hover:tw-text-slate-700 tw-border-transparent'}`}
              >
                Ekskluderte Kunder
              </button>
              <button
                onClick={() => setSettingsTab('excludedOrders')}
                className={`tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-rounded-t-lg tw-border tw-border-b-0 ${settingsTab === 'excludedOrders' ? 'tw-bg-blue-50 tw-text-blue-600 tw-border-blue-300 tw-border-b-2 tw-border-blue-600' : 'tw-text-slate-500 hover:tw-text-slate-700 tw-border-transparent'}`}
              >
                Ekskluderte Ordrer
              </button>
            </div>

            {/* Content */}
            <div className="tw-flex-1 tw-overflow-auto tw-p-6 tw-bg-slate-50/50">
              {settingsLoading ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-h-32">
                  <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600"></div>
                </div>
              ) : settingsTab === 'mapping' ? (
                <div>
                  <p className="tw-text-sm tw-text-slate-500 tw-mb-4">
                    Kartlegg Unimicro-selgere til lokale brukere. Automatisk forslag vises med grønn hak-merking.
                  </p>
                  <div className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg tw-overflow-hidden">
                  <table className="tw-w-full tw-text-sm">
                    <thead>
                      <tr className="tw-bg-slate-50 tw-border-b tw-border-slate-200 tw-text-left">
                        <th className="tw-py-3 tw-px-4 tw-font-semibold tw-text-slate-600">Unimicro Selger</th>
                        <th className="tw-py-3 tw-px-4 tw-font-semibold tw-text-slate-600">Nr</th>
                        <th className="tw-py-3 tw-px-4 tw-font-semibold tw-text-slate-600">Lokal Bruker</th>
                        <th className="tw-py-3 tw-px-4 tw-w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sellers.map(s => {
                        const mapping = getMappingForSeller(s.Nr);
                        const suggestedId = getAutoSuggestedUserId(s.Navn);
                        const currentUserId = mapping?.local_user_id || (suggestedId && !mapping ? suggestedId : '');
                        const isAutoSuggested = !mapping && suggestedId;
                        return (
                          <tr key={s.Nr} className="tw-border-b tw-border-slate-100">
                            <td className="tw-py-3 tw-px-4 tw-text-slate-700">
                              {s.Navn}
                              {isAutoSuggested && (
                                <span className="tw-ml-2 tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-green-600">
                                  <FiCheck size={12} /> Auto-forslag
                                </span>
                              )}
                            </td>
                            <td className="tw-py-3 tw-px-4 tw-text-slate-500">{s.Nr}</td>
                            <td className="tw-py-3 tw-px-4">
                              <select
                                value={currentUserId || ''}
                                onChange={(e) => handleSaveMapping(s.Nr, s.Navn, parseInt(e.target.value) || null)}
                                className="tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-slate-300 tw-rounded-lg tw-bg-white tw-outline-none focus:tw-border-blue-500 tw-w-full"
                              >
                                <option value="">— Ingen mapping —</option>
                                {localUsers.map(u => (
                                  <option key={u.id} value={u.id}>{u.name}</option>
                                ))}
                              </select>
                            </td>
                            <td className="tw-py-3 tw-px-4">
                              {mapping && (
                                <button
                                  onClick={() => handleDeleteMapping(s.Nr)}
                                  className="tw-text-slate-400 hover:tw-text-red-500"
                                  title="Fjern mapping"
                                >
                                  <FiTrash2 size={14} />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
              ) : settingsTab === 'excludedOrders' ? (
                <div>
                  <p className="tw-text-sm tw-text-slate-500 tw-mb-4">
                    Legg til ordrenummer (Ordrenr) fra Unimicro for å ekskludere spesifikke ordrer fra listen.
                  </p>

                  {/* Add new excluded order */}
                  <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4 tw-p-4 tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg">
                    <input
                      type="text"
                      placeholder="Ordrenr (f.eks. 12345)"
                      value={newExcludedOrdrenr}
                      onChange={e => setNewExcludedOrdrenr(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleAddExcludedOrder()}
                      className="tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-slate-300 tw-rounded-lg tw-outline-none focus:tw-border-blue-500 tw-w-48"
                    />
                    <input
                      type="text"
                      placeholder="Årsak (valgfritt, f.eks. 'Testordre')"
                      value={newExcludedOrderReason}
                      onChange={e => setNewExcludedOrderReason(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleAddExcludedOrder()}
                      className="tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-slate-300 tw-rounded-lg tw-outline-none focus:tw-border-blue-500 tw-flex-1"
                    />
                    <button
                      onClick={handleAddExcludedOrder}
                      disabled={!newExcludedOrdrenr.trim()}
                      className="tw-flex tw-items-center tw-gap-1 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-bg-blue-600 tw-border tw-border-blue-700 tw-rounded-lg hover:tw-bg-blue-700 disabled:tw-opacity-40 tw-transition-colors"
                    >
                      <FiPlus size={14} /> Legg til
                    </button>
                  </div>

                  {/* Excluded orders list */}
                  {excludedOrders.length === 0 ? (
                    <p className="tw-text-sm tw-text-slate-400 tw-py-4">Ingen ekskluderte ordrer</p>
                  ) : (
                    <div className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg tw-overflow-hidden">
                    <table className="tw-w-full tw-text-sm">
                      <thead>
                        <tr className="tw-bg-slate-50 tw-border-b tw-border-slate-200 tw-text-left">
                          <th className="tw-py-3 tw-px-4 tw-font-semibold tw-text-slate-600">Ordrenr</th>
                          <th className="tw-py-3 tw-px-4 tw-font-semibold tw-text-slate-600">Årsak</th>
                          <th className="tw-py-3 tw-px-4 tw-w-20"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {excludedOrders.map(o => (
                          <tr key={o.id} className="tw-border-b tw-border-slate-100">
                            <td className="tw-py-3 tw-px-4 tw-font-semibold tw-text-slate-700">{o.ordrenr}</td>
                            <td className="tw-py-3 tw-px-4 tw-text-slate-500">{o.reason || '—'}</td>
                            <td className="tw-py-3 tw-px-4">
                              <button
                                onClick={() => handleRemoveExcludedOrder(o.ordrenr)}
                                className="tw-text-slate-400 hover:tw-text-red-500"
                                title="Fjern ekskludering"
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p className="tw-text-sm tw-text-slate-500 tw-mb-4">
                    Legg til kundenummer (Kundenr) fra Unimicro for å ekskludere interne ordrer fra "Venter oppfølging".
                  </p>

                  {/* Add new excluded customer */}
                  <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4 tw-p-4 tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg">
                    <input
                      type="text"
                      placeholder="Kundenr (f.eks. 10001)"
                      value={newExcludedKundenr}
                      onChange={e => setNewExcludedKundenr(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleAddExcluded()}
                      className="tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-slate-300 tw-rounded-lg tw-outline-none focus:tw-border-blue-500 tw-w-48"
                    />
                    <input
                      type="text"
                      placeholder="Årsak (valgfritt, f.eks. 'Intern ordre')"
                      value={newExcludedReason}
                      onChange={e => setNewExcludedReason(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleAddExcluded()}
                      className="tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-slate-300 tw-rounded-lg tw-outline-none focus:tw-border-blue-500 tw-flex-1"
                    />
                    <button
                      onClick={handleAddExcluded}
                      disabled={!newExcludedKundenr.trim()}
                      className="tw-flex tw-items-center tw-gap-1 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-bg-blue-600 tw-border tw-border-blue-700 tw-rounded-lg hover:tw-bg-blue-700 disabled:tw-opacity-40 tw-transition-colors"
                    >
                      <FiPlus size={14} /> Legg til
                    </button>
                  </div>

                  {/* Excluded customers list */}
                  {excludedCustomers.length === 0 ? (
                    <p className="tw-text-sm tw-text-slate-400 tw-py-4">Ingen ekskluderte kunder</p>
                  ) : (
                    <div className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg tw-overflow-hidden">
                    <table className="tw-w-full tw-text-sm">
                      <thead>
                        <tr className="tw-bg-slate-50 tw-border-b tw-border-slate-200 tw-text-left">
                          <th className="tw-py-3 tw-px-4 tw-font-semibold tw-text-slate-600">Kundenr</th>
                          <th className="tw-py-3 tw-px-4 tw-font-semibold tw-text-slate-600">Kundenavn</th>
                          <th className="tw-py-3 tw-px-4 tw-font-semibold tw-text-slate-600">Årsak</th>
                          <th className="tw-py-3 tw-px-4 tw-w-20"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {excludedCustomers.map(c => (
                          <tr key={c.id} className="tw-border-b tw-border-slate-100">
                            <td className="tw-py-3 tw-px-4 tw-font-semibold tw-text-slate-700">{c.kundenr}</td>
                            <td className="tw-py-3 tw-px-4 tw-text-slate-600">{c.kundenavn || '—'}</td>
                            <td className="tw-py-3 tw-px-4 tw-text-slate-500">{c.reason || '—'}</td>
                            <td className="tw-py-3 tw-px-4">
                              <button
                                onClick={() => handleRemoveExcluded(c.kundenr)}
                                className="tw-text-slate-400 hover:tw-text-red-500"
                                title="Fjern ekskludering"
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnimicroOrdersWidget;
